import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import ParticleBackground from '@/components/ParticleBackground';

const heroImage = '/images/rakib-hero.webp';

const HeroSection = () => {
  const texts = ['SEO Expert', 'Digital Marketer'];
  const [textIndex, setTextIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentText = texts[textIndex];
    const typingSpeed = isDeleting ? 50 : 100;
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayedText.length < currentText.length) {
          setDisplayedText(currentText.slice(0, displayedText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayedText.length > 0) {
          setDisplayedText(currentText.slice(0, displayedText.length - 1));
        } else {
          setIsDeleting(false);
          setTextIndex(prev => (prev + 1) % texts.length);
        }
      }
    }, typingSpeed);
    return () => clearTimeout(timeout);
  }, [displayedText, isDeleting, textIndex]);

  return (
    <section id="home" className="min-h-screen flex items-center pt-20 pb-12 relative">
      <ParticleBackground />
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content - No animation delay for LCP */}
          <div className="space-y-6">
            <p className="text-sm uppercase tracking-wider text-muted-foreground font-semibold">
              Hello, I'm
            </p>
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight my-0 md:text-6xl">
              Rakib Hossen Topu
              <span className="sr-only"> - SEO Expert in BD and Digital Marketing Specialist Bangladesh</span>
            </h1>
            <div className="h-20 flex items-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                A <span className="gradient-text">{displayedText}<span className="animate-pulse">|</span></span>
              </h2>
            </div>
            <p className="text-lg text-muted-foreground max-w-xl">
              As a leading <strong>SEO Expert in BD</strong>, I drive business growth through strategic SEO and digital marketing. Specializing in search engine optimization, local SEO, YouTube marketing, and Meta Ads campaigns that deliver measurable results and maximize ROI for businesses across Bangladesh and worldwide.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Button variant="hero" size="lg" className="button-press group" onClick={(e) => {
                e.preventDefault();
                const element = document.getElementById('portfolio');
                if (element) {
                  const offsetTop = element.offsetTop - 80;
                  window.scrollTo({ top: offsetTop, behavior: 'smooth' });
                }
              }}>
                View Portfolio
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className="relative animate-fade-in-up flex items-end justify-center">
            {/* Organic Blob Shapes */}
            <div className="absolute inset-0 flex items-center justify-center -translate-y-8">
              <div 
                className="absolute w-[95%] h-[95%] bg-primary/25 animate-float"
                style={{
                  borderRadius: '60% 40% 55% 45% / 55% 60% 40% 45%',
                  animationDelay: '0s'
                }}
              />
              <div 
                className="absolute w-[90%] h-[90%] bg-primary/20 animate-float"
                style={{
                  borderRadius: '45% 55% 40% 60% / 60% 45% 55% 40%',
                  animationDelay: '0.5s'
                }}
              />
              <div 
                className="absolute w-[85%] h-[85%] bg-primary-glow/25 animate-float"
                style={{
                  borderRadius: '55% 45% 60% 40% / 40% 55% 45% 60%',
                  animationDelay: '1s'
                }}
              />
            </div>
            {/* Main Image */}
            <div className="relative z-10 w-full max-w-[400px] lg:max-w-[480px]">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-primary/30 blur-3xl rounded-full scale-75 -translate-y-4" />
              <img 
                src={heroImage} 
                alt="Rakib Hossen Topu - SEO Expert in BD & Digital Marketing Expert Bangladesh" 
                width={480}
                height={600}
                className="relative w-full h-auto object-contain drop-shadow-[0_0_40px_rgba(254,1,104,0.3)]"
                style={{ clipPath: 'inset(0 0 8% 0)' }}
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
